import { ToolDef, ToolAnswer, ToolContext } from '../mcp-types';

export type PageMode = 'preview' | 'spotlight';

/**
 * Map the Angular router URL to the screen it draws. Preview and Spotlight share a URL, as do the entity list and an
 * open entity, so this is the route only; `where_am_i` tells those apart.
 */
export function screenFromUrl(url: string): string {
  if (!url || url === '/') return 'sign-in';
  if (url.includes('/dashboard/client-list')) return 'select-project';
  if (url.includes('/dashboard/pages')) return 'pages';
  if (url.includes('/dashboard/pageData')) return 'page-data';
  if (url.includes('/ui-editor')) return 'page-editor';
  if (url.includes('/dashboard/entity-list')) return 'entity';
  if (url.includes('/dashboard/ootb-entity')) return 'ootb-entity';
  if (url.includes('/dashboard/users-workspaces')) return 'workspaces';
  if (url.includes('/dashboard/user-mgmt')) return 'team';
  if (url.includes('/dashboard/bastion')) return 'bastion';
  if (url.includes('/dashboard/modules')) return 'modules';
  if (url.includes('/function-editor')) return 'function-editor';
  if (url.includes('/schema-explorer')) return 'schema';
  if (url.includes('/query')) return 'query';
  if (url.includes('/chart-style')) return 'chart-style';
  return 'unknown';
}

// These screens draw one page, which only memory holds: a reload loses it and the editor falls back to the pages list.
const NEEDS_A_PAGE: ReadonlySet<string> = new Set(['page-editor', 'page-data', 'function-editor']);

export interface WhereAmIResult {
  screen: string;
  project: { id: string; name: string } | null;
  builder: { role: string };
  mode?: PageMode;
  tab?: string;
  panel?: string;
  /** Settings is an overlay: the screen still open underneath it. */
  behind?: string;
  page?: { id: string; name: string };
  entity?: { id: string; name: string };
  note?: string;
}

/** What only the live screen knows. Each answers for what is drawn now. */
export interface WhereAmIProbes {
  pageMode?: () => PageMode | null;
  settings?: () => { card: string | null } | null;
  panels?: () => { screen: string; tab?: string; panel?: string } | null;
}

/** Where the builder is, for every tool that has to say so. */
export function locate(
  ctx: ToolContext,
  getSelectedPage: () => { id: string; name: string } | null,
  getSelectedEntity: () => { id: string; name: string } | null,
  probes: WhereAmIProbes = {},
): WhereAmIResult {
  const route = screenFromUrl(ctx.routerUrl);
  const result: WhereAmIResult = {
    screen: route,
    project: ctx.projectId ? { id: ctx.projectId, name: ctx.projectName } : null,
    builder: { role: ctx.builderRole },
  };

  if (NEEDS_A_PAGE.has(route)) {
    const page = getSelectedPage();
    if (page?.id) {
      result.page = { id: page.id, name: page.name };
      if (route === 'page-editor') {
        result.mode = probes.pageMode?.() ?? 'preview';
        result.screen = result.mode === 'spotlight' ? 'page-spotlight' : 'page-preview';
      }
    } else {
      result.screen = 'pages';
      result.note = 'The page that was open did not survive a reload, so the builder is on the pages list. Open it again with navigate.';
    }
  }

  if (route === 'entity') {
    const entity = getSelectedEntity();
    if (entity) result.entity = { id: entity.id, name: entity.name };
    else result.screen = 'data';
  }

  const panels = probes.panels?.();
  if (panels?.screen === route) {
    if (panels.tab) result.tab = panels.tab;
    if (panels.panel) result.panel = panels.panel;
  }

  const settings = probes.settings?.();
  if (settings) {
    result.behind = result.screen;
    result.screen = 'settings';
    delete result.tab;
    delete result.panel;
    if (settings.card) result.panel = settings.card;
  }

  return result;
}

// Was (2026-10-30), when it knew five screens and refused with no project open. locate() answers now, so a project isn't needed:
// /** Map the Angular router URL to a human-readable screen name. */
// export function screenFromUrl(url: string): string {
//   if (!url || url === '/') return 'home';
//   if (url.includes('/dashboard/pages')) return 'pages';
//   if (url.includes('/ui-editor')) return 'page-editor';
//   if (url.includes('/dashboard/entity-list') || url.includes('/dashboard/ootb-entity')) return 'entity';
//   if (url.includes('/dashboard/users-workspaces') || url.includes('/dashboard/user-mgmt')) return 'workspaces';
//   if (url.includes('/dashboard/bastion')) return 'bastion';
//   if (url.includes('/dashboard/modules')) return 'modules';
//   if (url.includes('/function-editor')) return 'function-editor';
//   if (url.includes('/query') || url.includes('/schema-explorer')) return 'query';
//   if (url.includes('/chart-style')) return 'chart-style';
//   return 'home';
// }
//
//     async run(_input: Record<string, unknown>, ctx: ToolContext): Promise<ToolAnswer> {
//       const screen = screenFromUrl(ctx.routerUrl);
//       const result: WhereAmIResult = {
//         screen,
//         project: { id: ctx.projectId, name: ctx.projectName },
//         builder: { role: ctx.builderRole },
//       };
//
//       if (screen === 'page-editor') {
//         const page = getSelectedPage();
//         if (page?.id) result.page = { id: page.id, name: page.name };
//       }
//
//       if (screen === 'entity') {
//         const entity = getSelectedEntity();
//         if (entity?.id) result.entity = { id: entity.id, name: entity.name };
//       }
//
//       return { outcome: 'done', changed: [], result };
//     },

export function makeWhereAmITool(
  getSelectedPage: () => { id: string; name: string } | null,
  getSelectedEntity: () => { id: string; name: string } | null,
  probes: WhereAmIProbes = {},
): ToolDef {
  return {
    name: 'where_am_i',
    kind: 'read',
    worksWithoutProject: true,
    description: [
      'Says where the builder is in Zavo: the open project (null when none is), the screen, the mode (preview or',
      'spotlight) on a page, the tab and panel open, and the open page or entity (name and id) when there is one,',
      'plus the builder\'s role. Settings opens over another screen, which is given as "behind".',
      'Call it right after connect, and again whenever you are unsure what the builder is looking at.',
      'It changes nothing and needs no input.',
      'Returns structure only, never record values.',
    ].join(' '),
    inputSchema: { type: 'object', properties: {}, additionalProperties: false },

    async run(_input: Record<string, unknown>, ctx: ToolContext): Promise<ToolAnswer> {
      const result = locate(ctx, getSelectedPage, getSelectedEntity, probes);
      return { outcome: 'done', changed: [], result };
    },
  };
}
